// Copyright © 2021 Move Closer

import { AnyObject } from '@movecloser/front-core'

import { DriverConfig, RelatedRecord, RelatedType, RelatedTypeDriver, ResolvesDriver } from '../contracts'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export abstract class AbstractTypeDriver<M = AnyObject, D extends AnyObject = AnyObject> implements RelatedTypeDriver<M, D> {
  /**
   * Key that stores RelatedType.
   * @protected
   */
  protected abstract typeKey: RelatedType

  /**
   * @inheritDoc
   */
  public canDescribe (id: string, record: RelatedRecord): boolean {
    if (!this.hasTypeEntries(record)) {
      return false
    }

    return this.hasRelated(id, record)
  }

  /**
   * @inheritDoc
   */
  public dependentTypes (): RelatedType[] {
    return []
  }

  /**
   * @inheritDoc
   */
  public describe (id: string, record: RelatedRecord): D {
    this.throwWhenMissing(id, record)

    return { ...record[this.typeKey]![`${id}`] } as D
  }

  /**
   * @inheritDoc
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public resolve (id: string, record: RelatedRecord, config: DriverConfig, driverResolver?: ResolvesDriver): M {
    this.throwWhenMissing(id, record)

    return { ...record[this.typeKey]![`${id}`] } as M
  }

  /**
   * @inheritDoc
   */
  public reset (): void {
    // NOTICE! Should be implemented in drivers
  }

  /**
   * Determine if there's a related in the record.
   * @param id
   * @param record
   * @protected
   */
  protected hasRelated (id: string, record: RelatedRecord): boolean {
    const dictionary = record[this.typeKey]
    if (!dictionary) {
      return false
    }

    return (dictionary[`${id}`] && dictionary[`${id}`] !== null)
  }

  /**
   * Determine of there are entries of given type.
   * @param record
   * @protected
   */
  protected hasTypeEntries (record: RelatedRecord): boolean {
    return !!record[this.typeKey]
  }

  /**
   * Throws when related not found.
   * @param id
   * @param record
   * @protected
   */
  protected throwWhenMissing (id: string, record: RelatedRecord): void {
    if (!this.hasTypeEntries(record)) {
      // console.warn(`[RelatedService]: There are no entries for the [${this.typeKey}] [${id}] type in the record!`)
      throw new Error(
        `[RelatedService]: There are no entries for the [${this.typeKey}] type in the record!`
      )
    }

    if (!this.hasRelated(id, record)) {
      // console.warn(`[RelatedService]: There are no entries for the [${id}] ID & [${this.typeKey}] in the record!`)
      throw new Error(
        `[RelatedService]: There are no entries for the [${id}] ID & [${this.typeKey}] in the record!`
      )
    }
  }
}
